import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	ChangeEvent,
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
	useState,
} from 'react';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import { eightDigitPhone } from '@/utils/validationHelper';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	AGE_RANGE_LIST,
	LANGUAGE_LIST,
	SEX_LIST,
} from '@/common/constants/common';
import {
	useGetAreaItemListQuery,
	useGetAreaSubItemListMutation,
	useGetDicListQuery,
} from '@/api/common';
import { AreaDTO } from '@/api/DTO/common.interface';
import { Language } from '@/api/enum/common.enum';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditClientInformationForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const [getAreaSubItemList] = useGetAreaSubItemListMutation();
		const [districtList, setDistrictList] = useState<AreaDTO[]>([]);
		const [subDistrictList, setSubDistrictList] = useState<AreaDTO[]>([]);
		const [estateCourtVillageList, setEstateCourtVillageList] = useState<
			AreaDTO[]
		>([]);
		const [buildingBlockList, setBuildingBlockList] = useState<AreaDTO[]>([]);

		const { data: maritalStatusDicList } = useGetDicListQuery(
			'marital_status',
			{ refetchOnMountOrArgChange: true }
		);
		const { data: areaItemList } = useGetAreaItemListQuery(
			{ type: 0 },
			{ refetchOnMountOrArgChange: true }
		);

		const defaultValues: DefaultValues<any> = {
			addressLanguage: Language.chinese,
			sex: null,
		};

		useEffect(() => {
			if (details) {
				methods.reset(details);

				if (details.region) initAddress(details.region, 'region');
				if (details.district) initAddress(details.district, 'district');
				if (details.subdistrict)
					initAddress(details.subdistrict, 'subdistrict');
				if (details.estateCourtVillage)
					initAddress(details.estateCourtVillage, 'estateCourtVillage');
			}
		}, [details]);

		const formSchema = Yup.object().shape({
			nameEn: Yup.string().required(t('errorMessage.pleaseEnter')),
			nameZh: Yup.string().required(t('errorMessage.pleaseEnter')),
			phone: Yup.string()
				.required(t('errorMessage.pleaseEnter'))
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
			sex: Yup.string().required(t('errorMessage.pleaseSelect')),
			addressLanguage: Yup.string().required(t('errorMessage.pleaseSelect')),
			dateOfBirth: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
		});

		const watchAddressLanguage = methods.watch('addressLanguage');
		const watchDateOfBirth = methods.watch('dateOfBirth');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const initAddress = async (pid: string, type: string) => {
			if (!pid) return;
			const response = await getAreaSubItemList(pid || '');
			if ('data' in response) {
				if (type == 'region') setDistrictList(response?.data?.data || []);
				if (type == 'district') setSubDistrictList(response?.data?.data || []);
				if (type == 'subdistrict')
					setEstateCourtVillageList(response?.data?.data || []);
				if (type == 'estateCourtVillage')
					setBuildingBlockList(response?.data?.data || []);
			}
		};

		const handleChangeRegion = async (e: any, type: string) => {
			if (!e) return;
			const response = await getAreaSubItemList(e || '');

			if ('data' in response) {
				if (type == 'region') {
					methods.setValue('district', '');
					methods.setValue('subdistrict', '');
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setDistrictList(response?.data?.data || []);
				}
				if (type == 'district') {
					methods.setValue('subdistrict', '');
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setSubDistrictList(response?.data?.data || []);
				}
				if (type == 'subdistrict') {
					methods.setValue('estateCourtVillage', '');
					methods.setValue('buildingBlock', '');
					setEstateCourtVillageList(response?.data?.data || []);
				}
				if (type == 'estateCourtVillage') {
					methods.setValue('buildingBlock', '');
					setBuildingBlockList(response?.data?.data || []);
				}
			}
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.clientInformation')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameEn'
									label={t('preAdmission.name_en') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='nameZh'
									label={t('preAdmission.name_zh') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='dateOfBirth'
									label={t('preAdmission.dateOfBirth')}
									handleChange={() => {
										methods.setValue('ageRange', '');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									disabled={watchDateOfBirth}
									optional={true}
									control={methods.control}
									name='ageRange'
									label={t('preAdmission.ageRange')}
									options={AGE_RANGE_LIST}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.sex') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='sex'
									row
									options={SEX_LIST}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='phone'
									label={t('preAdmission.mobilePhone') + '*'}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									optional={true}
									control={methods.control}
									name='maritalStatus'
									label={t('preAdmission.maritalStatus')}
									options={(maritalStatusDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.addressLanguage') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='addressLanguage'
									row
									options={LANGUAGE_LIST}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.region')}
											options={(areaItemList?.data || []).map(
												(item: { nameEn: any }) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.district')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.subDistrict')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameEn,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate/court/village')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameEn,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}
							{watchAddressLanguage == Language.chinese && (
								<>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='region'
											label={t('preAdmission.region')}
											options={(areaItemList?.data || []).map(
												(item: { nameZh: any }) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'region');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='district'
											label={t('preAdmission.district')}
											options={(districtList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'district');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='subdistrict'
											label={t('preAdmission.subDistrict')}
											options={(subDistrictList || []).map((item: AreaDTO) => {
												return {
													...item,
													name: item.nameZh,
												};
											})}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'subdistrict');
											}}
										/>
									</Grid>
									<Grid size={6}>
										<AppDropDownMenu
											optional={true}
											control={methods.control}
											name='estateCourtVillage'
											label={t('preAdmission.estate/court/village')}
											options={(estateCourtVillageList || []).map(
												(item: AreaDTO) => {
													return {
														...item,
														name: item.nameZh,
													};
												}
											)}
											handleChange={(e: any) => {
												handleChangeRegion(e, 'estateCourtVillage');
											}}
										/>
									</Grid>
								</>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreet'
									label={t('preAdmission.road/street')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='roadStreetNo'
									label={t('preAdmission.road/streetNo')}
								/>
							</Grid>
							{watchAddressLanguage == Language.english && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.building/block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameEn,
											};
										})}
									/>
								</Grid>
							)}
							{watchAddressLanguage == Language.chinese && (
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='buildingBlock'
										label={t('preAdmission.building/block')}
										options={(buildingBlockList || []).map((item: AreaDTO) => {
											return {
												...item,
												name: item.nameZh,
											};
										})}
									/>
								</Grid>
							)}

							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='buildingBlockSpecify'
									label={t('preAdmission.building/block(specify)')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='floor'
									label={t('preAdmission.floor')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='flat'
									label={t('preAdmission.flat')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);
EditClientInformationForm.displayName = 'EditClientInformationForm';

export default EditClientInformationForm;
